import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardBody, Table } from "reactstrap";
import { formatDateForDisplay } from "utils/dateUtils";

const mapRepoStatus = (rawStatus, isOutForRepo, isRepossession, t) => {
  if (isRepossession === true) {
    return t("reposTab.status.repossessed");
  }

  if (typeof rawStatus === "boolean") {
    return rawStatus ? t("reposTab.status.outForRepo") : t("reposTab.status.cancelled");
  }

  if (rawStatus) {
    const normalized = String(rawStatus).toLowerCase();
    if (["out_for_repo", "out for repo", "out-for-repo"].includes(normalized)) {
      return t("reposTab.status.outForRepo");
    }
    if (["cancelled", "canceled", "cancel"].includes(normalized)) {
      return t("reposTab.status.cancelled");
    }
    if (["repossessed", "repo"].includes(normalized)) {
      return t("reposTab.status.repossessed");
    }
    return rawStatus;
  }

  if (typeof isOutForRepo === "boolean") {
    return isOutForRepo ? t("reposTab.status.outForRepo") : t("reposTab.status.cancelled");
  }

  return t("reposTab.status.unknown");
};

const normalizeRepoHistory = (data, t) => {
  if (!Array.isArray(data)) {
    return [];
  }

  return [...data]
    .map((item, index) => {
      const agentRaw = item?.is_repossession
        ? item?.recovered_repo || item?.repo_agent
        : item?.repo_agent;
      const agent =
        typeof agentRaw === "object" && agentRaw !== null
          ? agentRaw?.name || agentRaw?.full_name || agentRaw?.label || agentRaw?.value || ""
          : agentRaw || "";

      return {
        id: item?.id ?? item?.documentId ?? index,
        datePlacedOut:
          item?.date_placed_out ||
          item?.placed_out_at ||
          item?.createdAt ||
          item?.publishedAt ||
          item?.effective_date ||
          null,
        effectiveDate:
          item?.effective_date ||
          item?.effectiveDate ||
          item?.cancelled_at ||
          item?.updatedAt ||
          item?.createdAt ||
          null,
        reason: item?.reason || item?.repo_reason || "",
        agent,
        notes: item?.notes || item?.repo_notes || "",
        statusLabel: mapRepoStatus(
          item?.status,
          item?.is_out_for_repo,
          item?.is_repossession,
          t
        ),
      };
    })
    .sort((a, b) => {
      const aTime = a.datePlacedOut ? new Date(a.datePlacedOut).getTime() : 0;
      const bTime = b.datePlacedOut ? new Date(b.datePlacedOut).getTime() : 0;
      return bTime - aTime;
    });
};

const ReposTab = ({ data, isLightTheme = false }) => {
  const { t } = useTranslation();

  const repoHistory = useMemo(() => normalizeRepoHistory(data, t), [data, t]);
  const emptyTitleColor = isLightTheme ? "#344675" : "rgba(255, 255, 255, 0.85)";
  const emptyDescriptionColor = isLightTheme ? "#8898aa" : "rgba(255, 255, 255, 0.55)";

  return (
    <Card className="pt-0">
      {repoHistory.length === 0 ? (
        <CardBody
          className="d-flex flex-column align-items-center justify-content-center text-center"
          style={{ minHeight: 180, padding: "48px 24px" }}
        >
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: emptyTitleColor,
              marginBottom: 8,
            }}
          >
            {t("reposTab.noData")}
          </div>
          <div
            style={{
              fontSize: 13,
              lineHeight: 1.5,
              color: emptyDescriptionColor,
              maxWidth: 420,
            }}
          >
            {t("reposTab.emptyDescription")}
          </div>
        </CardBody>
      ) : (
        <div className="table-responsive pb-0" style={{fontSize: 13}}>
          <Table  hover size="sm" className="mb-0">
            <thead>
              <tr style={{ backgroundColor: "#0066a1", color:   isLightTheme ? "#fff" : "#fff" }}>
                <th style={{ color: isLightTheme ? "#fff" : "#fff" }}>{t("reposTab.table.datePlacedOut")}</th>
                <th style={{ color: isLightTheme ? "#fff" : "#fff" }}>Effective Date</th>
                <th style={{ color: isLightTheme ? "#fff" : "#fff" }}>{t("reposTab.table.reason")}</th>
                <th style={{ color: isLightTheme ? "#fff" : "#fff" }}>{t("reposTab.table.agent")}</th>
                <th style={{ color: isLightTheme ? "#fff" : "#fff" }}>{t("reposTab.table.notes")}</th>
                <th style={{ color: isLightTheme ? "#fff" : "#fff" }}>{t("reposTab.table.status")}</th>
              </tr>
            </thead>
            <tbody>
              {repoHistory.map((row) => (
                <tr key={row.id}>
                  <td>{formatDateForDisplay(row.datePlacedOut)}</td>
                  <td>{formatDateForDisplay(row.effectiveDate)}</td>
                  <td>{row.reason}</td>
                  <td>{row.agent}</td>
                  <td style={{ maxWidth: 320 }}>
                      <span title={row.notes} style={{ display: "inline-block" }}>
                        {row.notes}
                      </span>
                  </td>
                  <td>{row.statusLabel}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Card>
  );
};

export default ReposTab;
